import React, { useState } from "react";
import { Search, Settings, TrendingUp, IndianRupee, Edit2, LayoutGrid, List, CheckCircle2, X, Users, Ban } from "lucide-react";
import StatCard from "@/components/ui/StatCard";
import { usePaySetup } from "../hooks/usePaySetup";
import { PaySetupFormModal } from "../components/paysetup/PaySetupFormModal";
import { Pagination } from "@/components/ui/Pagination";
import { DataTable } from "@/components/ui/DataTable";
import toast from "react-hot-toast";

export function PaySetupScreen() {
  const {
    employees, allEmployees, paySetups,
    search, setSearch,
    isFormOpen, isSaving,
    formData, formErrors, computedTotals,
    selectedEmpId, selectedEmployee,
    openForm, closeForm,
    updateGrossSalary, updateField, updateComponent, addComponent, removeComponent, save,
    togglePayStatus,
    getPaySetup,
  } = usePaySetup();

  const [viewStyle, setViewStyle] = useState("grid");
  const [searchVisible, setSearchVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const handleSave = async () => {
    const ok = await save();
    if (ok) toast.success("Pay setup saved successfully");
  };

  const configuredCount = allEmployees.filter((e) => getPaySetup(e.id)).length;
  const totalPayroll = paySetups.reduce((s, p) => {
    const earnings = p.components.filter((c) => c.type === "earning").reduce((a, c) => a + c.amount, 0);
    const deductions = p.components.filter((c) => c.type === "deduction").reduce((a, c) => a + c.amount, 0);
    const pfRate = (p.employerPfRate !== undefined ? p.employerPfRate : 13) / 100;
    const esiRate = (p.employerEsiRate !== undefined ? p.employerEsiRate : 3.25) / 100;
    const employerPf = Math.round(earnings * pfRate);
    const employerEsi = (earnings > 0 && earnings <= 21000) ? Math.round(earnings * esiRate) : 0;
    const net = earnings - deductions;
    return s + net;
  }, 0);

  const totalPages = Math.ceil(employees.length / ITEMS_PER_PAGE) || 1;
  const paginatedEmployees = employees.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-400 bg-slate-50 min-h-screen pb-10">
        
        {/* Header Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard variant="luxury" title="Configured Setups" value={`${configuredCount} / ${allEmployees.length}`} topColor="border-t-emerald-500" iconHoverColor="group-hover:text-emerald-500" illustration={<CheckCircle2 className="w-10 h-10" strokeWidth={1.5} />} />
          <StatCard variant="luxury" title="Pending Config" value={allEmployees.length - configuredCount} topColor="border-t-amber-500" iconHoverColor="group-hover:text-amber-500" illustration={<Settings className="w-10 h-10" strokeWidth={1.5} />} />
          <StatCard variant="luxury" title="Total Employees" value={allEmployees.length} topColor="border-t-blue-500" iconHoverColor="group-hover:text-blue-500" illustration={<Users className="w-10 h-10" strokeWidth={1.5} />} />
          <StatCard variant="luxury" title="Monthly Payroll" value={`₹${totalPayroll.toLocaleString("en-IN")}`} topColor="border-t-purple-500" iconHoverColor="group-hover:text-purple-500" illustration={<IndianRupee className="w-10 h-10" strokeWidth={1.5} />} />
        </div>

        {/* Control Panel */}
        <div className="card p-2 sm:p-3 flex flex-wrap items-center gap-2 bg-white shadow-sm border border-slate-100 animate-in fade-in slide-in-from-top-1 rounded-md">

          {/* Search Toggle */}
          <button
            onClick={() => { setSearchVisible(v => !v); if (searchVisible) { setSearch(''); setCurrentPage(1); } }}
            className={`p-2 h-9 w-9 flex items-center justify-center rounded-md border transition-all ${
              searchVisible
                ? 'bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200'
                : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300'
            }`}
            title="Search Employee"
          >
            <Search size={16} />
          </button>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-md border border-slate-200 ml-auto">
            <button
              onClick={() => setViewStyle("grid")}
              className={`p-1.5 rounded transition-all duration-200 ${
                viewStyle === "grid"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              title="Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewStyle("list")}
              className={`p-1.5 rounded transition-all duration-200 ${
                viewStyle === "list"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              title="List View"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search Overlay */}
        {searchVisible && (
          <div className="card p-3 flex items-center gap-3 bg-white shadow-sm border border-slate-100 rounded-md animate-in fade-in slide-in-from-top-1">
            <Search size={16} className="text-slate-400 shrink-0" />
            <input
              type="text"
              autoFocus
              className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            {search && (
              <button onClick={() => { setSearch(''); setCurrentPage(1); }} className="text-slate-400 hover:text-slate-600">
                <X size={15} />
              </button>
            )}
          </div>
        )}

        {/* Render View Selection */}
        {viewStyle === "grid" ? (
          /* Grid (Block) View */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {paginatedEmployees.map((emp, idx) => {
              const setup = getPaySetup(emp.id);
              let net = null;
              if (setup) {
                const earnings = (setup.components || []).filter((c) => c.type === "earning").reduce((s, c) => s + c.amount, 0);
                const deductions = (setup.components || []).filter((c) => c.type === "deduction").reduce((s, c) => s + c.amount, 0);
                const pfRate = (setup.employerPfRate !== undefined ? setup.employerPfRate : 13) / 100;
                const esiRate = (setup.employerEsiRate !== undefined ? setup.employerEsiRate : 3.25) / 100;
                const employerPf = Math.round(earnings * pfRate);
                const employerEsi = (earnings > 0 && earnings <= 21000) ? Math.round(earnings * esiRate) : 0;
                net = earnings - deductions;
              }

              return (
                <div
                  key={emp.id}
                  className="bg-white rounded-2xl border border-slate-100 p-5 hover:border-blue-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group flex flex-col h-full"
                >
                  {/* Employee Header */}
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center border-2 border-white shadow-sm text-2xl">
                      {idx % 2 === 0 ? "👨‍💼" : "👩‍💼"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate">{emp.name}</p>
                      <p className="text-xs text-slate-500 truncate capitalize">{emp.designation?.replace("_", " ")}</p>
                      <p className="text-[10px] font-medium text-slate-400 mt-0.5">#{emp.id}</p>
                    </div>
                    <button
                      onClick={() => openForm(emp.id)}
                      className="w-8 h-8 rounded-full flex items-center justify-center border border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                      title={setup ? "Edit Pay Setup" : "Setup Pay"}
                    >
                      {setup ? <Edit2 className="w-3.5 h-3.5" /> : <Settings className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  <div className="flex-1 flex flex-col justify-end">
                    {setup ? (
                      <>
                        {/* Component breakdown */}
                        <div className="space-y-2 mb-4 bg-slate-50 rounded-xl p-3 border border-slate-100">
                          {setup.components.slice(0, 3).map((c, i) => (
                            <div key={i} className="flex justify-between items-center">
                              <span className="text-xs font-medium text-slate-600 truncate">{c.name}</span>
                              <span className={`text-xs font-bold ${c.type === "deduction" ? "text-red-500" : "text-slate-700"}`}>
                                {c.type === "deduction" ? "-" : ""}₹{Number(c.amount).toLocaleString("en-IN")}
                              </span>
                            </div>
                          ))}
                          {setup.components.length > 3 && (
                            <div className="pt-1 mt-1 border-t border-slate-200/60">
                              <p className="text-[10px] text-center font-medium text-slate-400">+{setup.components.length - 3} more components</p>
                            </div>
                          )}
                        </div>

                        {/* Net Pay */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 flex justify-between items-center border border-blue-100">
                          <span className="text-xs font-semibold text-blue-800">Net Pay / Month</span>
                          <span className="text-lg font-black text-blue-600">₹{Number(net).toLocaleString("en-IN")}</span>
                        </div>
                      </>
                    ) : (
                      <div className="bg-amber-50/50 border border-amber-100 border-dashed rounded-xl p-6 text-center h-full flex flex-col items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                          <TrendingUp className="w-5 h-5 text-amber-600" />
                        </div>
                        <p className="text-sm text-amber-800 font-semibold mb-1">Not Configured</p>
                        <p className="text-xs text-amber-600/70 mb-4 px-2">Setup a salary structure to generate payslips.</p>
                        <button
                          onClick={() => openForm(emp.id)}
                          className="text-xs font-bold bg-white border border-amber-200 text-amber-700 px-4 py-2 rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors shadow-sm"
                        >
                          Setup Now
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List (Table) View */
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
            <DataTable 
              columns={[
                {
                  header: "Employee",
                  key: "employeeName",
                  render: (emp, idx) => (
                    <div className="flex flex-col min-w-[150px]">
                      <span className="font-medium text-slate-700">{emp.name}</span>
                      <span className="text-xs text-slate-400 mt-0.5">{emp.phone || "—"}</span>
                    </div>
                  )
                },
                {
                  header: "Employee ID",
                  key: "employeeId",
                  render: (emp) => <span className="font-medium text-slate-500">#{emp.id}</span>
                },
                {
                  header: "Reporting Manager",
                  key: "reportingManager",
                  render: (emp) => <span className="font-medium text-slate-600">{emp.reportingManager || "—"}</span>
                },
                {
                  header: "Work Location",
                  key: "workLocation",
                  render: (emp) => <span className="font-medium text-slate-600">{emp.workLocation || "—"}</span>
                },
                {
                  header: "Designation",
                  key: "designation",
                  render: (emp) => <span className="font-medium text-slate-600 capitalize">{emp.designation?.replace("_", " ")}</span>
                },
                {
                  header: "Role",
                  key: "role",
                  render: (emp) => <span className="font-medium text-slate-600 capitalize">{emp.role?.replace("-", " ")}</span>
                },
                {
                  header: "Joining Date",
                  key: "joiningDate",
                  render: (emp) => <span className="font-medium text-slate-600">{emp.joiningDate ? new Date(emp.joiningDate).toLocaleDateString("en-IN") : "—"}</span>
                },
                {
                  header: "PF Number",
                  key: "pfNumber",
                  render: (emp) => {
                    const setup = getPaySetup(emp.id);
                    return <span className="font-medium text-slate-600">{setup?.pfNumber || "—"}</span>;
                  }
                },
                {
                  header: "ESI Number",
                  key: "esiNumber",
                  render: (emp) => {
                    const setup = getPaySetup(emp.id);
                    return <span className="font-medium text-slate-600">{setup?.esiNumber || "—"}</span>;
                  }
                },
                {
                  header: "Gross Salary",
                  key: "grossSalary",
                  render: (emp) => {
                    const setup = getPaySetup(emp.id);
                    return <span className="font-medium text-slate-600">{setup ? `₹${Number(setup.grossSalary).toLocaleString("en-IN")}` : "—"}</span>;
                  }
                },
                {
                  header: "Net Pay",
                  key: "netPay",
                  render: (emp) => {
                    const setup = getPaySetup(emp.id);
                    let net = null;
                    if (setup) {
                      const earnings = (setup.components || []).filter((c) => c.type === "earning").reduce((s, c) => s + c.amount, 0);
                      const deductions = (setup.components || []).filter((c) => c.type === "deduction").reduce((s, c) => s + c.amount, 0);
                      const pfRate = (setup.employerPfRate !== undefined ? setup.employerPfRate : 13) / 100;
                      const esiRate = (setup.employerEsiRate !== undefined ? setup.employerEsiRate : 3.25) / 100;
                      const employerPf = Math.round(earnings * pfRate);
                      const employerEsi = (earnings > 0 && earnings <= 21000) ? Math.round(earnings * esiRate) : 0;
                      net = earnings - deductions;
                    }
                    return <span className={`font-medium ${setup ? 'text-blue-600' : 'text-slate-400'}`}>{setup ? `₹${Number(net).toLocaleString("en-IN")}` : "—"}</span>;
                  }
                },
                {
                  header: "Status",
                  key: "status",
                  render: (emp) => {
                    const setup = getPaySetup(emp.id);
                    return (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        setup 
                          ? (setup.isPayDisabled ? "bg-red-50 text-red-600 border border-red-100" : "bg-emerald-50 text-emerald-600 border border-emerald-100") 
                          : "bg-amber-50 text-amber-600 border border-amber-100"
                      }`}>
                        {setup ? (setup.isPayDisabled ? "Disabled" : "Configured") : "Pending"}
                      </span>
                    );
                  }
                },
                {
                  header: "Actions",
                  key: "actions",
                  className: "text-right pr-4 min-w-[100px]",
                  render: (emp) => {
                    const setup = getPaySetup(emp.id);
                    return (
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openForm(emp.id)}
                          className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                          title={setup ? "Edit Pay Setup" : "Configure Pay"}
                        >
                          <IndianRupee className="w-3.5 h-3.5" />
                        </button>
                        {setup && (
                          <button
                            onClick={() => togglePayStatus(emp.id)}
                            className={`w-8 h-8 rounded-full inline-flex items-center justify-center border transition-colors ${
                              setup.isPayDisabled 
                                ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50" 
                                : "border-slate-200 text-slate-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200"
                            }`}
                            title={setup.isPayDisabled ? "Enable Pay" : "Disable Pay"}
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  }
                }
              ]}
              data={paginatedEmployees}
              tableClassName="min-w-[1400px]"
            />
          </div>
        )}

        {/* Pagination */}
        {employees.length > 0 && (
          <div className="mt-4">
            <Pagination 
              page={currentPage} 
              totalPages={totalPages} 
              onPageChange={setCurrentPage} 
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <PaySetupFormModal
        isOpen={isFormOpen}
        onClose={closeForm}
        employee={selectedEmployee}
        formData={formData}
        formErrors={formErrors}
        computedTotals={computedTotals}
        onUpdateGross={updateGrossSalary}
        onUpdateField={updateField}
        onUpdateComponent={updateComponent}
        onAddComponent={addComponent}
        onRemoveComponent={removeComponent}
        onSubmit={handleSave}
        isSaving={isSaving}
      />
    </>
  );
}
